import * as React from 'react'

import { ChevronLeft, Users } from 'lucide-react'
import { observer } from 'mobx-react'

import Avatar from '~/client/src/shared/components/Avatar/Avatar'
import SearchInput from '~/client/src/shared/components/Filters/SearchInput/SearchInput'
import { Button } from '~/client/src/shared/components/shadcn-ui/button'
import {
  Empty,
  EmptyHeader,
  EmptyTitle,
} from '~/client/src/shared/components/shadcn-ui/empty'
import AvatarType from '~/client/src/shared/enums/AvatarType'
import Localization from '~/client/src/shared/localization/LocalizationManager'
import User from '~/client/src/shared/models/User'

import { Chat, Contact } from '../core/protocol/types'
import { ChatSession } from '../core/stores/Session'
import { WebContactDirectory } from '../web/directory'

// Text starts after a small avatar (32) and its 10px gutter.
const SEPARATOR_INSET = 42

interface IProps {
  session: ChatSession
  directory: WebContactDirectory
  onOpenChat: (chat: Chat) => void
  onOpenDraft: (recipient: Contact) => void
  onNewGroup: () => void
  onBack: () => void
}

function NewChatScreen({
  session,
  directory,
  onOpenChat,
  onOpenDraft,
  onNewGroup,
  onBack,
}: IProps): React.ReactElement {
  const [searchKey, setSearchKey] = React.useState('')
  const [resolvingId, setResolvingId] = React.useState<string>(null)
  const [resolveError, setResolveError] = React.useState(false)

  const userId = session.config.userId

  const members = directory.listMembers(userId).filter(member => {
    const key = searchKey.trim().toLowerCase()
    if (!key) {
      return true
    }
    return (
      member.fullName.toLowerCase().includes(key) ||
      directory.directSubtitle(member.id).toLowerCase().includes(key)
    )
  })

  const handlePick = async (member: User): Promise<void> => {
    if (resolvingId) {
      return
    }
    setResolveError(false)
    setResolvingId(member.id)

    const resolution = await session.resolveDirectChat(member.id)
    setResolvingId(null)

    switch (resolution.kind) {
      case 'existing':
        onOpenChat(resolution.chat)
        return
      case 'draft': {
        const recipient = directory.toContact(member.id)
        if (recipient) {
          onOpenDraft(recipient)
        }
        return
      }
      case 'error':
        setResolveError(true)
    }
  }

  const renderMemberRow = (member: User, index: number): React.ReactElement => (
    <React.Fragment key={member.id}>
      {index > 0 && (
        <div
          className="tw:h-px tw:bg-border"
          style={{ marginLeft: SEPARATOR_INSET }}
        />
      )}
      <div
        className={`tw:flex tw:cursor-pointer tw:items-center tw:py-2.5 ${
          resolvingId === member.id
            ? 'tw:pointer-events-none tw:opacity-60'
            : ''
        }`}
        onClick={() => handlePick(member)}
      >
        <Avatar
          avatarType={AvatarType.User}
          src={member.avatarUrl}
          isOnline={session.inbox.isOnline(member.id)}
        />
        <div className="tw:ml-2.5 tw:flex tw:min-w-0 tw:flex-1 tw:flex-col">
          <span className="tw:truncate tw:font-semibold tw:text-foreground">
            {member.fullName}
          </span>
          <span className="tw:truncate tw:text-xs tw:text-muted-foreground">
            {directory.directSubtitle(member.id)}
          </span>
        </div>
      </div>
    </React.Fragment>
  )

  return (
    <div className="tw:flex tw:h-full tw:flex-col">
      <div className="tw:flex tw:items-center tw:gap-1.5 tw:px-4 tw:pb-2.5 tw:pt-3">
        <Button variant="primary-ghost" size="icon" onClick={onBack}>
          <ChevronLeft />
        </Button>
        <span className="tw:flex-1 tw:text-center tw:text-base tw:font-semibold tw:text-foreground">
          {Localization.translator.newChat}
        </span>
        <span className="tw:w-8" />
      </div>

      <div className="tw:px-4 tw:pb-2.5">
        <SearchInput
          value={searchKey}
          onChange={setSearchKey}
          placeholder={Localization.translator.searchUser}
          className="tw:w-full"
        />
      </div>

      <Button
        variant="primary-ghost"
        onClick={onNewGroup}
        className="tw:mx-1.5 tw:mb-1 tw:self-start"
      >
        <Users />
        {Localization.translator.newGroup}
      </Button>

      {resolveError && (
        <span className="tw:pb-2.5 tw:text-center tw:text-xs tw:text-destructive">
          {Localization.translator.couldNotOpenConversation}
        </span>
      )}

      {members.length ? (
        <div className="tw:min-h-0 tw:flex-1 tw:overflow-y-auto tw:px-4 tw:pb-2.5">
          {members.map(renderMemberRow)}
        </div>
      ) : (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{Localization.translator.noPeopleFound}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}
    </div>
  )
}

export default observer(NewChatScreen)
